import { WithDisposable } from '@blocksuite/block-std';
import { css, html, LitElement } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import { INPUT_FONT_SM, INPUT_FONT_XS } from '../../../../_common/styles.js';
import { stopPropagation } from '../../../../_common/utils/event.js';

@customElement('edgeless-chroma')
export class EdgelessChroma extends WithDisposable(LitElement) {
  static override styles = css`
    :host {
      display: flex;
      flex-direction: column;
      align-items: normal;
      gap: 12px;
      min-width: 198px;
      padding: 8px;
    }

    nav {
      display: flex;
      padding: 2px;
      align-items: flex-start;
      gap: 4px;
      align-self: stretch;
      border-radius: 8px;
      background: var(--affine-hover-color);
    }

    nav button {
      display: flex;
      padding: 4px 8px;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      flex: 1 0 0;

      ${INPUT_FONT_XS}
      color: var(--affine-text-secondary-color);
      font-weight: 600;

      border-radius: 8px;
      background: transparent;
      border: none;
    }

    nav button[actived] {
      color: var(--affine-text-primary-color, #121212);
      background: var(--affine-white);
      box-shadow: var(--affine-shadow-1);
      pointer-events: none;
    }

    .modes {
      display: none;
      gap: 8px;
      align-self: stretch;
    }
    .modes[actived] {
      display: flex;
    }

    .modes .mode {
      display: flex;
      padding: 2px;
      flex-direction: column;
      flex: 1 0 0;
    }

    .modes .mode button {
      position: relative;
      display: flex;
      height: 60px;
      padding: 12px 12px 8px;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      gap: 4px;
      align-self: stretch;
      border-radius: 8px;
      border: 1px solid var(--affine-border-color);
      box-sizing: border-box;

      ${INPUT_FONT_XS}
      font-weight: 400;
      color: var(--affine-text-secondary-color);
    }
    .modes .mode.light button {
      background: white;
    }
    .modes .mode.dark button {
      background: #141414;
    }
    .modes .mode button .color {
      background: red;
      flex-shrink: 0;
      width: 22px;
      height: 22px;
      border-radius: 50%;
      overflow: hidden;
    }
    .modes .mode button[actived] {
      pointer-events: none;
      outline: 2px solid var(--affine-brand-color);
    }

    .content {
      display: flex;
      flex-direction: column;
      gap: inherit;
    }

    .color-area {
      position: relative;
      color: red;
      width: 100%;
      height: 170px;
    }

    .color-area::before {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      left: 0;
      bottom: 0;
      background-image: linear-gradient(rgba(0, 0, 0, 0), #000),
        linear-gradient(90deg, #fff, currentColor);
      border-radius: 4px;
      overflow: hidden;
    }

    .color-area::after {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      left: 0;
      bottom: 0;
      border: 1px solid rgba(0, 0, 0, 0.1);
      box-sizing: border-box;
      border-radius: 4px;
      overflow: hidden;
    }

    .color-circle {
      position: absolute;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 0.5px solid #e3e2e4;
      border-radius: 50%;
      box-sizing: border-box;
      box-shadow: 0px 0px 0px 0.5px #e3e3e4 inset;
      filter: drop-shadow(0px 0px 12px rgba(66, 65, 73, 0.14));
      pointer-events: none;
    }
    .color-circle::before {
      content: '';
      border-style: solid;
      border-color: white;
      border-radius: 50%;
      box-sizing: border-box;
    }

    .color-area .color-circle {
      left: 100px;
      top: 10px;
      left: 100px;
      width: 25px;
      height: 25px;
      background: red;
    }
    .color-area .color-circle::before {
      width: 24px;
      height: 24px;
      background: red;
      border-width: 4px;
    }

    .color-slider {
      display: flex;
      align-items: center;
      position: relative;
      width: 100%;
      height: 12px;
      margin-top: 4px;
      margin-bottom: 12px;
    }
    .color-slider::before {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      left: 0;
      bottom: 0;
      background-image: linear-gradient(
        to left,
        #ff0008 0,
        #ff00eb 16.6667%,
        #0e00ff 32.8125%,
        #00edff 49.0625%,
        #00ff00 67.9688%,
        #fffe00 83.125%,
        #ff0800 100%
      );
      border-radius: 12px;
      overflow: hidden;
    }

    .color-slider .color-circle {
      left: 100px;
      width: 21px;
      height: 21px;
      background: red;
    }
    .color-slider .color-circle::before {
      width: 20px;
      height: 20px;
      background: red;
      border-width: 2px;
    }

    footer {
      display: flex;
      justify-content: space-between;
    }

    .field {
      display: flex;
      padding: 7px 9px;
      align-items: center;
      border-radius: 8px;
      gap: 4px;
      border: 1px solid var(--affine-border-color);
      background: var(--affine-white-30);
      box-sizing: border-box;
    }

    .field.color {
      width: 134px;
    }

    .field.opacity {
      width: 56px;
    }

    input {
      display: flex;
      width: 100%;
      padding: 0;
      background: transparent;
      border: none;
      outline: none;
      ${INPUT_FONT_SM}
    }
  `;

  @state()
  accessor tabKey: 'colors' | 'custom' = 'colors';

  @state()
  accessor modeKey: 'light' | 'dark' = 'light';

  override firstUpdated() {
    this.disposables.addFromEvent(this, 'pointerdown', stopPropagation);
    this.disposables.addFromEvent(this, 'click', stopPropagation);
  }

  override render() {
    return html`
      <header>
        <nav>
          <button
            ?actived=${this.tabKey === 'colors'}
            @click=${() => (this.tabKey = 'colors')}
          >
            Colors
          </button>
          <button
            ?actived=${this.tabKey === 'custom'}
            @click=${() => (this.tabKey = 'custom')}
          >
            Custom
          </button>
        </nav>
      </header>

      <div class="modes" ?actived=${this.tabKey === 'custom'}>
        <div class="mode light">
          <button
            ?actived=${this.modeKey === 'light'}
            @click=${() => (this.modeKey = 'light')}
          >
            <div class="color"></div>
            <div>Light</div>
          </button>
        </div>
        <div class="mode dark">
          <button
            ?actived=${this.modeKey === 'dark'}
            @click=${() => (this.modeKey = 'dark')}
          >
            <div class="color"></div>
            <div>Dark</div>
          </button>
        </div>
      </div>

      <div class="content">
        <div class="color-area">
          <div class="color-circle"></div>
        </div>
        <div class="color-slider">
          <div class="color-circle"></div>
        </div>
      </div>

      <footer>
        <div class="field color">
          <label for="color-value">#</label>
          <input id="color-value" type="text" placeholder="000" />
        </div>
        <div class="field opacity">
          <input type="text" placeholder="100%" />
        </div>
      </footer>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'edgeless-chroma': EdgelessChroma;
  }
}
